import { HttpClient } from '@angular/common/http';
import { Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { ContextService, ServerServiceConfig, LoginData } from '../context.service';
import { Subscription } from 'rxjs';
import { environment } from '../../environments/environment';

interface UserStatus {
  term: string,
  applicationNumber: string,
  program: string,
  degree: string,
  createdDate: Date | undefined,
  modifiedDate?: Date | undefined,
  studentType?: string | undefined
}

const MAXCOUNT = 20;
const ACADEMIC_CATEGORY = 'Student Category';
@Component({
  selector: 'mutara-apply-status',
  templateUrl: './apply-status.component.html',
  styleUrls: ['./apply-status.component.scss'],
  standalone: false
})
export class ApplyStatusComponent implements OnInit, OnDestroy {
  login: LoginData | undefined;
  config: ServerServiceConfig | undefined;
  timer: number | undefined;
  countTimer: number = 0;
  isVertical: boolean = false;
  status: UserStatus = {
    term: '',
    createdDate: undefined,
    applicationNumber: 'UNKNOWN',
    program: '',
    degree: '',
  };
  subscriptions: Subscription[] = [];

  constructor(private context: ContextService, private http: HttpClient, private router: Router) {
    this.subscriptions.push(this.context.subscribeTo("orientation", (isVertical: boolean) => {
      this.isVertical = isVertical;
    }));
  }
  /** Trick the view to vertical if too small */
  // @HostListener('window:resize', [])
  // onResize() {
  //   this.context.setOrientation(window.innerWidth <= 950 ? 'vertical' : 'horizontal');
  // }
  get debug(): boolean {
    return environment.production;
  }

  ngOnInit(): void {
    // Alway start with the cylon  
    this.context.setProgressIndeterminate(true);
    this.context.showProgress(true);

    this.subscriptions.push(this.context.subscribeTo("applicationInfo", (config: ServerServiceConfig) => {
      this.config = config;
    }));

    this.subscriptions.push(this.context.subscribeTo("loginData", (login: LoginData) => {
      this.login = login;
      if (login.username == '') {
        if (!!this.timer) {
          this.stopTimer();
        }
      } else {
        if (!!this.login && !!this.login?.username && !['NEW', 'READY', 'PROCESS', 'QUEUE'].includes(this.status.applicationNumber ?? 'NEW')) {
          if (this.debug) { console.log('Starting status timer...'); }
          this.stopTimer();
          this.getStatus();
          this.timer = setInterval(() => {
            if (['NEW', 'READY', 'PROCESS', 'QUEUE'].includes(this.status.applicationNumber)) {
              this.getStatus();
            } else {
              this.stopTimer();
            }
          }, 5 * 1000);
        } else {
          this.stopTimer();
        }
      }
    }));
    // Only force a login if the username is empty (been cleared)
    this.context.pleaseLogin(this.login?.username == '');
  }
  ngOnDestroy(): void {
    this.subscriptions.forEach(sub => sub.unsubscribe());
    this.subscriptions = [];
  }
  /**
   * Stop polling the services for data
   */
  stopTimer() {
    if (!!this.timer) {
      clearInterval(this.timer);
      this.timer = undefined;
      this.countTimer = -1;
      this.context.setProgressIndeterminate(false);
      this.context.setPercentComplete(100);
    }
  }
  /**
   * Request the current status from the services
   */
  getStatus() {
    this.subscriptions.push(this.http.get('$service/admission-status').subscribe({
      error: (err) => {
        this.stopTimer();
        return err;
      },
      next: (results: any) => {
        this.status = results[0];
        this.status.createdDate = new Date(Date.parse(results[0].createdDate));
        this.status.modifiedDate = new Date(Date.parse(results[0].modifiedDate));
        // Retry this every period until ERROR or ID or over MAXCOUNT
        if (++this.countTimer >= MAXCOUNT || !['NEW', 'READY', 'PROCESS', 'QUEUE'].includes(this.status.applicationNumber ?? 'NEW')) {
          if (this.debug) { console.log('Stopping status timer %O', this.status.applicationNumber); }
          this.stopTimer();
        }
        if (this.status.applicationNumber == 'ERROR') {
          this.stopTimer();
          // Wait for 10 seconds before the logout activity
        }
        if (this.countTimer >= MAXCOUNT || this.countTimer == -1) {
          this.stopTimer();
        }
      }
    }));
  }

  /**
   * Get the label for the status page based on the last know status request
   * @returns <string>
   */
  get applicationState() {
    let appStatus = 'Application ';
    switch ((this.status?.applicationNumber ?? 'NEW')) {
      case 'NEW':
        appStatus = appStatus + 'submitted for processing';
        break;
      case 'QUEUE':
      case 'PROCESS':
        appStatus = appStatus + 'pending...';
        break;
      case 'ERROR':
        appStatus = appStatus + 'Submission Error';
        break;
      default:
        appStatus = appStatus + 'Successfully Submitted';
        break;
    }
    return appStatus;
  }

  /** WIP This needs to come back from the services */
  get studentType() {
    let value = 'TODO-LOOKUP';
    if (!!this.status?.studentType) {
      let found = this.config?.lookups?.[ACADEMIC_CATEGORY]?.find(option => option.value == this.status.studentType);
      if (!!found) {
        value = found.label;
      }
    }
    return ('AEIOU'.includes(value.charAt(0).toUpperCase()) ? 'an ' : 'a ') + value;
  }

  get programName() {
    let progName = this.status?.program;
    if ('GENERAL' == (progName ?? 'GENERAL')) {
      progName = 'Non-Degree Program';
    }
    progName = progName.trim();
    if (!progName.toUpperCase().endsWith('PROGRAM')) {
      progName += ' Program';
    }
    return progName;
  }

  get submitDate() {
    return this.status?.modifiedDate?.toLocaleString();
  }

  get inProcess() {
    return this.status.applicationNumber == 'UNKNOWN';
  }

  navigateReapply() {
    console.log('User requested new application');
    this.stopTimer();
    this.context.pleaseLogin(false);
    this.context.nextMessage('logout', true);
    this.router.navigate(['/apply'], { replaceUrl: true });
  }
}
